'use client';
import { type Array, Exit } from 'effect';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { openCartDrawerAtom } from '@vs-shop/engine/atom/ui/cartDrawer';
import type { CartLineRequest } from '../effect/atom/order/cartMutations';
import {
  cartVariantStatusAtom,
  cartVariantStatusLookupAtom,
} from '../effect/atom/order/cartMutations';
import { addToCartAtomFn } from '../effect/atom/order/orderMutations';
import type { AddProductVariantToOrderDataProduct } from '../effect/flow/order/addProductVariantToOrder';
import { captureCause } from '../shared/utils/sentry';
import { showErrorToast } from '@vs-shop/ui/toast';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

export type AddToCartRequest = CartLineRequest<AddProductVariantToOrderDataProduct>;

/**
 * Adds lines to the cart and opens the drawer in the same frame. The drawer
 * shows the lines as pending until the backend confirms them;
 * `CartRejectionAnnouncer` reports a line the backend refuses.
 */
export function useAddToCart() {
  const intl = useIntl();
  const addToCart = useAtomSet(addToCartAtomFn, { mode: 'promiseExit' });
  const setIsCartDrawerOpen = useAtomSet(openCartDrawerAtom);

  return useCallback(
    (requests: Array.NonEmptyReadonlyArray<AddToCartRequest>) => {
      setIsCartDrawerOpen(true);
      return addToCart(requests).then((result) =>
        Exit.match(result, {
          onFailure: (cause) => {
            captureCause(cause, {
              component: 'useAddToCart',
              action: 'Add to cart command failed',
              additionalData: {},
            });
            showErrorToast(
              intl.formatMessage({
                id: 'error.orderMutation.generic',
                defaultMessage: 'Došlo k chybě při zpracování objednávky.',
              })
            );
          },
          onSuccess: () => {},
        })
      );
    },
    [addToCart, intl, setIsCartDrawerOpen]
  );
}

/**
 * What the cart holds of one variant, confirmed or pending, and whether an add
 * of it is still on its way. Stock guards count the pending quantity too, so a
 * second click cannot request more than the stock allows.
 */
export function useVariantInCart(variantCode: string) {
  return useAtomValue(cartVariantStatusAtom(variantCode));
}

/**
 * `useVariantInCart` for a list of variants that one component renders
 * together, where a hook per variant is not possible.
 */
export function useVariantInCartLookup() {
  return useAtomValue(cartVariantStatusLookupAtom);
}
